
















































































































/* General */
import Vue from 'vue'
import { uniq } from 'lodash'

/* Mixins */

/* Components */
import LinkedItemDrawerDropdown from '@/components/item/linked-item-drawer-dropdown.vue'

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { ILabelValuePair } from '@/types/data-objects/global'
import { IBreadcrumbItemMenuItem } from '@/types/data-objects/items'
import { IAttachment } from '@/types/data-objects/attachments'
import {
  IItemReference,
  ILinkedItemForListing,
  ITasksOutputDetail,
  ITasksOutputHistory,
} from '@/api/types/response.types'
import { EEntities } from '@/types'

/* API */
import TasksAPI from '@/api/modules/tasks.api'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'

export default Vue.extend({
  name: 'TaskDetailDrawer',

  components: {
    LinkedItemDrawerDropdown,
  },

  props: {
    showDrawer: {
      type: Boolean,
      default: false,
    },
    originatingItem: {
      type: Object as () => IItemReference,
      required: true,
    },
    task: {
      type: Object as () => ILinkedItemForListing,
      required: true,
    },
    prevItem: {
      type: Object as () => ILinkedItemForListing,
      required: false,
      default: null,
    },
    nextItem: {
      type: Object as () => ILinkedItemForListing,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      EEntities,
      ColorAssistant,
      drawerOpened: false,
      isDataLoaded: false,
      isAccessible: false,
      isRequestingAccess: false,
      projectsRequestedAccessTo: [] as ILabelValuePair[],
      details: {} as ITasksOutputDetail,
      globalAttachments: [] as IAttachment[],
      history: [] as ITasksOutputHistory[],
    }
  },

  computed: {
    visible: {
      get(): Boolean {
        return this.showDrawer
      },
      set(newValue: boolean) {
        this.$emit('update:showDrawer', newValue)
      },
    },
    drawerWidthPct(): string {
      if (this.$screen.isMobile(this.$mq)) {
        return '100%'
      }
      return '524px'
    },
    prevItemForNav(): IBreadcrumbItemMenuItem {
      if (!this.prevItem) {
        return null
      }
      return {
        itemID: this.prevItem.id,
        entityID: this.prevItem.entity.id,
        entityName: this.prevItem.entity.name,
        entityType: TextAssistant.entitySingularName(EEntities.PROJECTS),
        refNumber: this.prevItem.refNumber,
        title: this.prevItem.title,
      }
    },
    nextItemForNav(): IBreadcrumbItemMenuItem {
      if (!this.nextItem) {
        return null
      }
      return {
        itemID: this.nextItem.id,
        entityID: this.nextItem.entity.id,
        entityName: this.nextItem.entity.name,
        entityType: TextAssistant.entitySingularName(EEntities.PROJECTS),
        refNumber: this.nextItem.refNumber,
        title: this.nextItem.title,
      }
    },
    requestedAccessToProject(): boolean {
      return this.projectsRequestedAccessTo.some(p => {
        return p.value === this.task.entity.id
      })
    },
    projectRequestMessage(): string {
      return this.projectsRequestedAccessTo.find(p => {
        return p.value === this.task.entity.id
      }).label
    },
    unlinkLabel(): string {
      return 'Unlink from #' + this.originatingItem.refNumber
    },
  },

  watch: {
    task: {
      /* Subsequent data fetches (prev/next arrows). Check for drawerOpened to avoid
         double fetching on initial task load */
      async handler(value) {
        if (this.drawerOpened) {
          await this.fetchData()
        }
      },
    },
    drawerOpened: {
      /* Initial data fetch should happen only after drawer finishes opening */
      async handler(value) {
        if (value) {
          await this.fetchData()
        }
      },
    },
  },

  methods: {
    async onOpened() {
      this.drawerOpened = true
    },

    onClosed() {
      this.drawerOpened = false
      this.isDataLoaded = false
      this.clearData()
    },

    clearData() {
      this.details = {} as ITasksOutputDetail
      this.globalAttachments = [] as IAttachment[]
      this.history = [] as ITasksOutputHistory[]
    },

    async fetchData() {
      this.isDataLoaded = false
      try {
        await Promise.all([this.fetchHistory(), this.fetchTaskData()])
        this.isAccessible = true
      } catch (error) {
        if (error.status === 403) {
          this.isAccessible = false
        } else {
          this.$error.emit(error, true)
        }
      } finally {
        this.isDataLoaded = true
      }
    },

    async fetchHistory() {
      const response = await TasksAPI.get_History(
        this.task.entity.id,
        this.task.id,
        'chron',
      )

      const allAttachments: IAttachment[] = []
      response.forEach(item => {
        allAttachments.push(...item.attachments)
      })
      this.globalAttachments = uniq(allAttachments)
      this.history = response
    },

    async fetchTaskData() {
      this.details = await TasksAPI.get_Detail(
        this.task.entity.id,
        this.task.id,
      )
    },

    async requestAccess() {
      try {
        this.isRequestingAccess = true
        const response = await InternalProjectsAPI.put_RequestAccess(
          this.task.entity.id,
        )
        const projectAccessRequested = {
          label: response.message,
          value: this.task.entity.id,
        }
        this.projectsRequestedAccessTo.push(projectAccessRequested)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isRequestingAccess = false
      }
    },

    goToTask(newWindow?: boolean) {
      RouteAssistant.goToTask(
        this.$router,
        this.$route.params.accountId,
        this.task.entity.id,
        this.task.id,
        this.$permissions,
        this.task.entity.name,
        this.task.refNumber,
        this.task.title,
        newWindow,
      )
    },
  },
})
